import { makeAutoObservable } from "mobx";

import {
  fetchRule,
  fetchRules,
  fetchRulesetWithDefinitions,
} from "@pages/registry/api";
import { Rule, RuleDefinition } from "@pages/registry/types";

interface AddressInterface {
  fullName: string;
  fetch: () => Promise<Rule & RuleDefinition>;
}

// Address of a rule that's coming from the registry
export class RegistryAddress implements AddressInterface {
  path: string;

  constructor(path: string) {
    makeAutoObservable(this);
    this.path = path;
  }

  get fullName(): string {
    return `${this.path}`;
  }

  async fetch() {
    const response = await fetchRules(this.fullName);
    return response[0];
  }
}

// Address of a rule that's a snippet alias
// name in format username:rule-id
export class SnippetAliasAddress implements AddressInterface {
  namespace: string;
  name: string;

  constructor(fullName: string) {
    makeAutoObservable(this);
    // these empty strings are just to make typescript happy
    this.namespace = "";
    this.name = "";
    // this setter actually fills namespace & name properly
    if (!fullName.includes(":")) {
      throw new Error(`Snippet alias ${fullName} has invalid name`);
    }
    this.fullName = fullName;
  }

  get fullName(): string {
    return `${this.namespace}:${this.name}`;
  }

  set fullName(newValue: string) {
    const [namespace, name] = newValue.split(":");
    this.namespace = namespace;
    this.name = name;
  }

  async fetch() {
    const response = await fetchRulesetWithDefinitions(this.fullName);
    return response.rules[0];
  }
}

// Address of a snippet
// Id is random characters
export class SnippetAddress implements AddressInterface {
  id: string;

  constructor(id: string) {
    makeAutoObservable(this);
    this.id = id;
  }

  get fullName(): string {
    return this.id;
  }

  async fetch() {
    const response = await fetchRule(this.fullName);
    return response;
  }
}

export type Address = RegistryAddress | SnippetAliasAddress | SnippetAddress;
